import { phone as phoneLib, nationalId as nationalIdLib } from '@parsina/framework';

import { jalaliToMs, jDaysInMonth, msToJalali } from './jalali';
import { clinicNow } from './clinicZone';
import { toEnDigits } from './utils';

// What the clinic considers a registerable patient. Registration happens in two
// places — the ثبت بیمار جدید screen and the «بیمار جدید» tab of the booking modal —
// and a patient created from a booking must be the same first-class record as one
// created from the screen. These rules live here so the two cannot drift apart.

// The insurer as it is STORED. `patient.insurance` is a free-text column, so
// this exact string is what goes on the wire, what comes back, and what a read
// is matched against (AddPatient.web.jsx maps a patient back onto a radio by
// comparing to it). It is therefore DATA and never follows the UI language: an
// English panel that stored "Social Security" would write a value no Persian
// panel — and no report — could ever match again.
//
// i18n-exempt: stored value, not UI copy. The label a person READS is `fa`
// below, which does follow the language.
// The four labels this client used to WRITE into `patients.insurance` before the
// insurer catalog existed, mapped onto the catalog ids they became.
//
// Read-only history. Nothing writes these any more — a patient's insurer is now a
// row in `patient_insurance_policies` keyed by catalog id, and the legacy column
// is DERIVED from it by patientmanagement. This map exists solely so an existing
// record still lights up the right option when the desk opens it.
//
// Both hamza spellings of تامین are here on purpose: the two were written by
// different versions of this file, and to any query they were different insurers —
// which is precisely why an unjoinable display string stopped being the model.
const LEGACY_INSURANCE_TO_ID = {
  'تامین اجتماعی': 'tamin',
  'تأمین اجتماعی': 'tamin',
  'بیمه سلامت': 'salamat',
  'بیمهٔ سلامت': 'salamat',
  'نیروهای مسلح': 'armed',
  'سایر / آزاد': 'azad',
  'سایر/آزاد': 'azad',
  'آزاد': 'azad',
};

/**
 * The catalog id for a legacy `patients.insurance` string, or null.
 *
 * Null means "recorded, but not one of ours" — the caller leaves the select
 * empty rather than guessing, because silently picking an insurer would change
 * what the clinic bills.
 */
export const insurerIdFromLegacy = (label) => {
  const key = (label || '').trim().replace(/\s+/g, ' ');
  return LEGACY_INSURANCE_TO_ID[key] || null;
};

// The national id. Its RULE is the kit's (PM-0076) — this file keeps no checksum of
// its own — and it belongs to a COUNTRY: the clinic's by default, another one for a
// patient from elsewhere (the field's country chip). `country` omitted is Iran.
// Validated when entered — but it is NOT a lookup key any more: one person can hold
// two records (an old and a new mobile), so a کد ملی no longer names one patient.
// The mobile is what the desk looks a patient up by; see LOOKUP_PATIENTS_BY_PHONE.
//
// Persian and Arabic digits are READ, not dropped: `digitsOnly` below deletes them,
// and running a کد ملی through it lost every code typed on a Persian keyboard (PM-0077).
export const validateNid = (nid, country) => nationalIdLib.isValidNationalId(nid, country);
/** `null`, or why the code cannot be saved: 'incomplete' | 'invalid'. Empty is fine. */
export const nidProblem = (nid, country) => nationalIdLib.nationalIdProblem(nid, { country });
/** What goes on the wire: ASCII, separators dropped, `null` for none. */
export const nidForWire = (nid, country) => nationalIdLib.normalizeNationalId(nid, country) || null;

/**
 * A country with no national-id entry in the kit: its GENERIC rule (5–20 letters or
 * digits, no checksum). For a clinic abroad whose country is not known yet — its
 * currency names none and no office line says — so the field neither holds the
 * patient to Iran's کد ملی nor claims a country it cannot name.
 */
export const GENERIC_NID_COUNTRY = 'ZZ';

/**
 * The country a national id is judged by: the one the desk picked on the field's
 * chip for a patient from elsewhere (PM-0076), else the CLINIC's — `useClinicRegion`
 * (PM-0061). `null` is Iran's rule, the default of every helper here.
 */
export function nidCountryFor(picked, region) {
  if (picked) return picked;
  if (!region || region.iran) return null;
  return region.country || GENERIC_NID_COUNTRY;
}

export const digitsOnly = s => (s || '').replace(/\D/g, '');

// ── The national id at a clinic abroad ───────────────────────────────────────
//
// `iranian` is the clinic's answer (hooks/useClinicRegion.js), never the
// patient's. Iran's کد ملی has a shape and a check digit, and a desk that typed
// one wrong is told so. Abroad the field holds whatever the patient's card says —
// Iraq's twelve-digit البطاقة الوطنية, a passport number with letters in it — and
// the platform knows the rule for none of them, so it judges none: a rule it
// cannot state is a patient it cannot register. patientmanagement folds the
// digits and stores the rest as typed (national_id.py judges no shape either).

/** Whether `nid` may be registered: empty, or valid where the clinic is. */
export function nationalIdOk(nid, { iranian = true } = {}) {
  if (!iranian) return true;
  const digits = digitsOnly(nid);
  return digits.length === 0 || validateNid(digits);
}

/**
 * The national id as it goes on the wire: Iran's as its digits, anyone else's as
 * typed — trimmed, in ASCII digits, letters kept.
 */
export function nationalIdForWire(nid, { iranian = true } = {}) {
  return iranian ? digitsOnly(nid) : toEnDigits(String(nid ?? '')).trim();
}


// ── تاریخ تولد ───────────────────────────────────────────────────────────────

/**
 * The oldest a patient may be registered as.
 *
 * The bound is for the TYPO — a کد ملی that landed in the date box, a year picked a
 * century off — not a statement about longevity, so it is a single upper limit and
 * nothing else. It MIRRORS `MAX_AGE_YEARS` in the clinic BFF (src/lib/birthDate.js)
 * and in patientmanagement (app/domain/birth_date.py), which is what actually refuses
 * the write; this copy exists so the desk is told under the field rather than by a
 * refusal from three services away. Change one, change all three.
 */
export const MAX_AGE_YEARS = 100;

// `YYYY-MM-DD` and `YYYY/MM/DD`. The picker writes the first (its value contract is
// Gregorian, it only DISPLAYS Jalali); the second is how the doctor mobile app writes
// the same column, and how a dictated date can come back.
const BIRTH_DATE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;

/**
 * A birth date as Gregorian `{ y, m, d }`, or null when the value says nothing.
 *
 * Null for empty, partial («۱۳۷۰»), malformed and impossible dates — every case that
 * carries no claim about an age which could be judged. An unknown birth date is a
 * legitimate state of a record and is never what this refuses.
 *
 * The calendar is read off the YEAR, the same way the BFF's `jalaliBirthYear` reads
 * it: no Jalali year in use is above 1500 and no patient's Gregorian year is below
 * 1800, so the ranges cannot overlap.
 */
export function parseBirthDate(value) {
  const match = BIRTH_DATE.exec(toEnDigits(value ?? '').trim());
  if (!match) return null;
  const [y, m, d] = match.slice(1).map(Number);

  if (y >= 1200 && y <= 1500) {
    if (m < 1 || m > 12 || d < 1 || d > jDaysInMonth(y, m)) return null;
    const g = new Date(jalaliToMs(y, m, d));
    return { y: g.getUTCFullYear(), m: g.getUTCMonth() + 1, d: g.getUTCDate() };
  }
  if (y < 1800) return null;
  // Date NORMALISES an impossible date rather than rejecting it — 1977-02-30 becomes
  // 2 March — so surviving the round trip is the check.
  const probe = new Date(Date.UTC(y, m - 1, d));
  const survives = probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  return survives ? { y, m, d } : null;
}

/**
 * Full years lived, or null when the date says nothing.
 *
 * Today is the day at the CLINIC — `clinicNow` (PM-0088) — not the day on the operator's machine, so a
 * laptop left on another zone cannot move the boundary. Negative for a future date,
 * which this does not judge: the picker already refuses one (`disableFuture`).
 */
export function ageInYears(value, now = clinicNow()) {
  const born = parseBirthDate(value);
  if (!born) return null;
  const [ty, tm, td] = [now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate()];
  const hadBirthday = tm > born.m || (tm === born.m && td >= born.d);
  return ty - born.y - (hadBirthday ? 0 : 1);
}

/**
 * True only for a date that puts the patient over `MAX_AGE_YEARS`.
 *
 * Exactly 100 passes — «بیش از ۱۰۰ سال» is the refusal, so the hundredth year is still
 * a registrable one.
 */
export function exceedsMaxAge(value, now) {
  const age = ageInYears(value, now);
  return age !== null && age > MAX_AGE_YEARS;
}

/**
 * The birth date an AGE stands for, when the desk knows only «۴۵ سال» (PM-0133):
 * 1 فروردین of (this Jalali year − age), on the clinic's clock. It is the date
 * voice registration already writes for a dictated age (PM-0083), so a typed and
 * a spoken «۴۵ سال» land on the same record — and `ageInYears` of it reads back
 * the age that was typed, from now until the next نوروز.
 *
 * Gregorian `YYYY-MM-DD` (the picker's value contract), or '' for an empty box or
 * anything that is not a whole number of years. An age past MAX_AGE_YEARS still
 * gets its date ON PURPOSE: the form's own bound (`exceedsMaxAge`) then refuses it
 * under the field, exactly as it refuses that date picked from the calendar — one
 * rule and one message, and the value on screen is always the value that is saved.
 */
export function birthDateFromAge(age, now = clinicNow()) {
  const years = parseMeasure(age);
  // 999 is only the box's three digits: any age it can hold gets a date (see above).
  if (years === null || !Number.isInteger(years) || years < 0 || years > 999) return '';
  const today = msToJalali(now.getTime());
  if (!today) return '';
  const born = new Date(jalaliToMs(today.y - years, 1, 1));
  const pad = (n) => String(n).padStart(2, '0');
  return `${born.getUTCFullYear()}-${pad(born.getUTCMonth() + 1)}-${pad(born.getUTCDate())}`;
}

// ── قد و وزن (PM-0122) ───────────────────────────────────────────────────────
// Bounds that catch the typo that is not a person — a height typed in metres
// («1.75»), a weight with a digit too many («720») — not clinical reference
// ranges. The BFF refuses a weight outside the same numbers (lib/bodyMeasures.js).
export const HEIGHT_CM = Object.freeze({ min: 20, max: 250 });
export const WEIGHT_KG = Object.freeze({ min: 0.5, max: 400 });

/**
 * A measurement box's value as a number: `null` for an empty box, `NaN` for one
 * that holds something else. Any keyboard's digits, and «٫» or «,» as the point.
 *
 * `parseFloat` was the parser before, and it reads «۱۷۵» as NaN — so a height
 * typed on a Persian keyboard reached the wire as `null` and was silently lost.
 */
export function parseMeasure(value) {
  // U+066B is «٫», the Persian/Arabic decimal separator.
  const text = toEnDigits(String(value ?? '')).replace(/[\u066B,]/g, '.').trim();
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : Number.NaN;
}

const outside = (value, { min, max }) => {
  const n = parseMeasure(value);
  return n !== null && !(n >= min && n <= max);
};

/** True for a height that was entered and is not a person's. An empty box is fine. */
export const heightProblem = (value) => outside(value, HEIGHT_CM);
/** True for a weight that was entered and is not a person's. An empty box is fine. */
export const weightProblem = (value) => outside(value, WEIGHT_KG);

/**
 * A stored height as its box's value: '' for «no height» (PM-0131). `height` is a
 * plain proto3 float, so a patient nobody measured arrives as 0 — and a box opened
 * on «0» was refused by `heightProblem`, holding the whole form hostage. A height
 * ≤ 0 is never a person's; anything else opens as stored, typo included, to be fixed.
 */
export const heightForBox = (height) => (Number(height) > 0 ? String(height) : '');

// ── شماره پرونده (PM-0128) ───────────────────────────────────────────────────
// The clinic's own number for the patient — on the clinic↔patient link, unique per
// clinic. Letters and digits of any script plus / - _ . («95/12», «1402-153»,
// «A.120»), no spaces, at most FILE_NUMBER_MAX characters. The BFF holds the same
// rule (lib/fileNumber.js) and refuses what gets past this (FILE_NUMBER_INVALID).
export const FILE_NUMBER_MAX = 20;
const FILE_NUMBER_SHAPE = /^[\p{L}\p{N}/._-]+$/u;

/** ASCII digits, trimmed — «۱۲» and «12» are one number. Empty = no file number. */
export const normalizeFileNumber = (value) => toEnDigits(String(value ?? '')).trim();

/** '' when the number may be saved (an empty one included), else 'tooLong' | 'invalid'. */
export function fileNumberProblem(value) {
  const number = normalizeFileNumber(value);
  if (!number) return '';
  if (number.length > FILE_NUMBER_MAX) return 'tooLong';
  if (!FILE_NUMBER_SHAPE.test(number)) return 'invalid';
  return '';
}

/**
 * Which `validation.fileNumber.*` sentence a failed save means, or '' when the
 * failure is not about the file number: `taken` (another patient holds it at this
 * clinic) or the BFF's own shape refusal.
 */
export function fileNumberRefusal(err) {
  const code = err?.extensions?.code;
  if (code === 'FILE_NUMBER_TAKEN') return 'taken';
  if (code === 'FILE_NUMBER_INVALID') return err.extensions.reason === 'tooLong' ? 'tooLong' : 'invalid';
  return '';
}

// ── آدرس و معرف (PM-0124) ─────────────────────────────────────────────────────
// Free text, optional. آدرس is the patient's own (every clinic holding the record
// reads it); معرف — who referred the patient — is THIS clinic's note, kept on its
// link. The BFF refuses longer (TEXT_TOO_LONG, lib/patientFields.js); the boxes cap
// at the same lengths so the desk never meets that refusal.
export const ADDRESS_MAX = 300;
export const REFERRER_MAX = 100;

// The required fields, as every registration surface enforces them. کد ملی is
// optional — the desk may not have it on hand — but if one is entered it must be a
// real, valid national ID rather than a partial or garbage value. At a clinic
// abroad (`iranianNid: false`) it is taken as typed — see `nationalIdOk`.
//
// The mobile is REQUIRED, with one honest way out: `noPhone`, the desk's explicit
// «بیمار شماره موبایل ندارد». An empty box on its own is not that answer — it is
// indistinguishable from a number nobody asked for, and the BFF refuses it
// (`PHONE_REQUIRED`) for the same reason.
//
// بیمه is OPTIONAL (PM-0125). A patient registered without one simply has no
// policy row — the state every patient registered before policies existed is
// already in — and the desk adds the insurer from the record when it is known.
// Every caller already skips the policy write when no insurer was chosen.
export function isRegisterable({ name, nid, nidCountry, phone, noPhone, birth, iranianNid = true }) {
  const phoneOk = !!noPhone || phoneLib.isValidPhone(String(phone || '').trim());
  return (name || '').trim().length >= 2
    // The issuing country the caller resolved (`nidCountryFor`); a caller that
    // only knows whether the clinic is in Iran passes `iranianNid`.
    && !nidProblem(nid, nidCountry || (iranianNid ? null : GENERIC_NID_COUNTRY))
    && phoneOk
    // تاریخ تولد is OPTIONAL, so an absent one registers exactly as before — but a
    // date that is PRESENT has to be a possible one. The surfaces that collect no
    // birth date pass none and are untouched.
    && !exceedsMaxAge(birth);
}

// Every record on a mobile number. A mobile is a HOUSEHOLD — a mother books for her
// children on her own number — so the answer is a list, never a match. A candidate
// the clinic has not linked comes back masked and without `patient`: its details are
// another clinic's to show, and the desk only learns enough to recognise the person.
export const LOOKUP_PATIENTS_BY_PHONE = `
  query LookupPatientsByPhone($phone: String!) {
    lookupPatientsByPhone(phone: $phone) {
      phoneNormalized
      candidates {
        patientId displayName isLinkedToThisClinic fileNumber birthYear gender hasAppAccount
        patient {
          id name phone birthDate nationalId gender
          bloodType insurance maritalStatus linkageStatus
        }
      }
    }
  }
`;

// Attach an existing record to this clinic. The record is global; what the desk
// creates here is the link, never a second patient.
export const LINK_EXISTING_PATIENT = `
  mutation LinkExistingPatient($patientId: ID!) {
    linkExistingPatient(patientId: $patientId) {
      id name phone nationalId
    }
  }
`;

/**
 * What an `addPatient` refusal says about the MOBILE, or null when it is about
 * something else.
 *
 *   PHONE_HAS_PATIENTS — the number already carries records, and the desk had not
 *     said it was registering someone new on it. `candidates` are the same rows the
 *     lookup returns (without `patient`), so the caller shows the same list.
 *   PHONE_REQUIRED     — an empty mobile without «ندارد».
 *
 * Both reach a caller because `bffClient` copies `extensions` onto the thrown error.
 */
export function phoneRegistrationError(err) {
  const code = err?.extensions?.code;
  if (code === 'PHONE_HAS_PATIENTS') {
    return { code, candidates: Array.isArray(err.extensions.candidates) ? err.extensions.candidates : [] };
  }
  if (code === 'PHONE_REQUIRED') return { code, candidates: [] };
  return null;
}

/**
 * The two phone flags on `CreatePatientInput`, sent only when TRUE.
 *
 * Omitted rather than `false` so a registration still goes through a BFF that
 * predates the fields — a false flag means exactly what an absent one does.
 */
export function phoneFlags({ noPhone, allowSharedPhone }) {
  return {
    ...(noPhone ? { noPhone: true } : {}),
    ...(allowSharedPhone && !noPhone ? { allowSharedPhone: true } : {}),
  };
}
